import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, MapPin, Phone, Linkedin, Github, Send, ArrowUpRight, Eye, Copy, Check } from 'lucide-react';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { profile } from '../mock';
import { toast } from 'sonner';

const Contact = () => {
  const [form, setForm] = useState({ name: '', email: '', company: '', message: '' });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error('Please fill in name, email and message.');
      return;
    }
    setLoading(true);
    setTimeout(() => {
      const all = JSON.parse(localStorage.getItem('contact_messages') || '[]');
      all.push({ ...form, at: new Date().toISOString() });
      localStorage.setItem('contact_messages', JSON.stringify(all));
      setLoading(false);
      setForm({ name: '', email: '', company: '', message: '' });
      toast.success('Message sent! I\'ll be in touch within 24 hours.');
    }, 900);
  };

  return (
    <section id="contact" className="relative py-32 bg-background overflow-hidden">
      <div className="absolute inset-0 bg-grid-dark opacity-20 pointer-events-none" />
      <div
        className="absolute -top-20 left-1/2 -translate-x-1/2 w-[800px] h-[400px] rounded-full opacity-[0.06] blur-[100px] pointer-events-none"
        style={{ background: 'radial-gradient(ellipse, #d97757 0%, transparent 70%)' }}
      />

      <div className="relative max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-12 gap-14 items-start">
          {/* Left: Heading + Info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-5"
          >
            <span className="mono text-xs uppercase tracking-widest text-primary mb-4 block">// 07 — Get in touch</span>
            <h2 className="serif text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.1] mb-6">
              Let's architect<br />
              something <span className="italic text-primary">remarkable</span>.
            </h2>
            <p className="text-slate-400 text-base leading-relaxed mb-10 max-w-md">
              Have a migration on the horizon, a landing zone to design, or just want to bounce architecture ideas? Drop a note — I read every message.
            </p>

            <div className="space-y-3 mb-10">
              <ContactRow icon={Mail}   label="Email"    value={profile.email}    href={`mailto:${profile.email}`}    masked />
              <ContactRow icon={Phone}  label="Phone"    value={profile.phone}    href={`tel:${profile.phone.replace(/\s/g, '')}`} masked />
              <ContactRow icon={MapPin} label="Location" value={profile.location} />
            </div>

            <div className="flex items-center gap-3">
              <SocialBtn href={profile.socials.linkedin} Icon={Linkedin} label="LinkedIn" />
              <SocialBtn href={profile.socials.github}   Icon={Github}   label="GitHub"   />
            </div>
          </motion.div>

          {/* Right: Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="lg:col-span-7"
          >
            <div className="relative">
              {/* Glow border */}
              <div className="absolute -inset-px rounded-3xl bg-gradient-to-br from-primary/30 via-transparent to-primary/10 blur-sm pointer-events-none" />
              <form
                onSubmit={handleSubmit}
                className="relative bg-card rounded-3xl p-8 sm:p-10 border border-white/8"
              >
                <div className="absolute inset-0 bg-grid-dark opacity-30 rounded-3xl pointer-events-none" />
                <div className="relative">
                  <div className="flex items-center justify-between mb-8">
                    <div>
                      <div className="mono text-xs uppercase tracking-widest text-primary mb-2">// new project</div>
                      <h3 className="text-white text-2xl font-semibold">Tell me about it</h3>
                    </div>
                    <span className="hidden sm:flex items-center gap-2 text-xs mono text-slate-500">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> Replying within 24h
                    </span>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Your name">
                      <Input name="name" value={form.name} onChange={handleChange} placeholder="Jane Doe"
                        className="bg-white/5 border-white/8 text-white placeholder:text-slate-600 h-12 rounded-xl focus-visible:ring-primary" />
                    </Field>
                    <Field label="Email">
                      <Input name="email" type="email" value={form.email} onChange={handleChange} placeholder="jane@company.com"
                        className="bg-white/5 border-white/8 text-white placeholder:text-slate-600 h-12 rounded-xl focus-visible:ring-primary" />
                    </Field>
                  </div>

                  <div className="mt-4">
                    <Field label="Company (optional)">
                      <Input name="company" value={form.company} onChange={handleChange} placeholder="Acme Corp"
                        className="bg-white/5 border-white/8 text-white placeholder:text-slate-600 h-12 rounded-xl focus-visible:ring-primary" />
                    </Field>
                  </div>

                  <div className="mt-4">
                    <Field label="Your message">
                      <Textarea name="message" value={form.message} onChange={handleChange} rows={5}
                        placeholder="Briefly describe your goals, timeline and any constraints…"
                        className="bg-white/5 border-white/8 text-white placeholder:text-slate-600 rounded-xl focus-visible:ring-primary resize-none" />
                    </Field>
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="mt-6 w-full sm:w-auto bg-primary hover:bg-primary/90 text-white px-8 py-3 h-auto rounded-full font-medium tracking-wide disabled:opacity-50 flex items-center gap-2"
                  >
                    {loading ? 'Sending…' : (<><Send size={15} /> Send message</>)}
                  </Button>
                </div>
              </form>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="mono text-[10px] uppercase tracking-widest text-slate-500 mb-2 block">{label}</span>
    {children}
  </label>
);

/* ---- masked reveal ---- */
const DECODE_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
const maskValue = (v) => v.split('').map(c => /[\s@.+\-]/.test(c) ? c : '●').join('');

const DecodeReveal = ({ value }) => {
  const [revealed, setRevealed] = useState(false);
  const [display,  setDisplay]  = useState(() => maskValue(value));
  const [copied,   setCopied]   = useState(false);

  const decode = () => {
    if (revealed) return;
    let step = 0;
    const iv = setInterval(() => {
      setDisplay(
        value.split('').map((char, i) => {
          if (/[\s@.+\-]/.test(char)) return char;
          if (i < step) return char;
          return DECODE_CHARS[Math.floor(Math.random() * DECODE_CHARS.length)];
        }).join('')
      );
      step++;
      if (step > value.length) { clearInterval(iv); setDisplay(value); setRevealed(true); }
    }, 22);
  };

  const copy = (e) => {
    e.preventDefault();
    try { navigator.clipboard?.writeText(value); } catch (_) {}
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className={`font-medium mono text-sm tracking-wider transition-colors ${revealed ? 'text-white' : 'text-slate-500 select-none'}`}>
        {display}
      </span>
      {!revealed ? (
        <button onClick={decode}
          className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 border border-primary/30 text-primary text-[10px] mono uppercase tracking-widest hover:bg-primary hover:text-white transition-colors">
          <Eye size={10} /> reveal
        </button>
      ) : (
        <button onClick={copy}
          className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-[10px] mono uppercase tracking-widest hover:bg-emerald-500 hover:text-white transition-colors">
          {copied ? <><Check size={10} /> copied</> : <><Copy size={10} /> copy</>}
        </button>
      )}
    </div>
  );
};

const ContactRow = ({ icon: Icon, label, value, href, masked }) => {
  const inner = (
    <div className="group flex items-center gap-4 p-4 rounded-2xl bg-white/4 border border-white/6 hover:border-primary/40 hover:bg-white/6 transition-all duration-300">
      <div className="w-10 h-10 rounded-xl bg-primary/10 border border-primary/20 text-primary flex items-center justify-center group-hover:bg-primary group-hover:text-white transition-colors shrink-0">
        <Icon size={17} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="mono text-[9px] uppercase tracking-widest text-slate-600 mb-0.5">{label}</div>
        {masked ? <DecodeReveal value={value} /> : <div className="text-white font-medium text-sm">{value}</div>}
      </div>
      {href && !masked && <ArrowUpRight size={16} className="text-slate-600 group-hover:text-primary group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all shrink-0" />}
    </div>
  );
  return href && !masked ? <a href={href}>{inner}</a> : inner;
};

const SocialBtn = ({ href, Icon, label }) => (
  <a href={href} target="_blank" rel="noreferrer" aria-label={label}
    className="w-11 h-11 rounded-xl bg-white/5 border border-white/8 hover:border-primary hover:bg-primary flex items-center justify-center text-slate-400 hover:text-white transition-all duration-300">
    <Icon size={17} />
  </a>
);

export default Contact;
